import { useContext, useMemo, useState } from "react";
import Table from "../../../Common/Table/Table";
import Button from "../../../Common/Button/Button";
import uploadIcon from "../../../Assets/Images/resume-upload-icon.png";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import FileIcon from "../../../Common/Common-Function/IconMapping.js";
import { DynamicContext } from "../../../Context/DynamicContext.js";
import DotLoader from "../../../Common/Pre-Loader/DotLoader.jsx";

const parseJobPreview = (preview) => {
  if (!preview) return {};
  if (typeof preview === "object") return preview;
  try {
    return JSON.parse(preview);
  } catch (error) {
    console.warn("Unable to parse job_preview:", error);
    return { raw_preview: preview };
  }
};

const getFileName = (file) =>
  typeof file === "string" ? file : file?.name || file?.file_name || "Unknown file";

const getJobStatus = (job = {}) => {
  const eventName = `${job.event_type || job.eventType || job.message_type || ""}`.toLowerCase();
  const status = `${job.job_creation_status || job.processing_status || job.status || ""}`.toLowerCase();

  if (status.includes("incomplete") || eventName.includes("incomplete")) return "incomplete";
  if (status === "created" || status === "updated") return status;
  if (status.includes("fail") || status === "error" || eventName.includes("-err") || eventName.includes("error")) return "failed";
  if (job.metadata?.error) return "failed";
  if (job.job_id) return "created";
  return "processing";
};

function JobJDResults(props) {
  const { isDarkMode } = useContext(DynamicContext);
  const {
    jobResults = props?.JDResults || props?.jdResults || [],
    setJobResults = props?.setJDResults || props?.setJdResults,
    uploadedFiles = props?.UploadedJDs || props?.uploadedJDs || props?.UploadedFiles || [],
    setUploadedFiles = props?.setUploadedJDs || props?.setUploadedFiles,
    failedJobs = props?.failedJDs || props?.failedJobs || [],
    setFailedJobs = props?.setFailedJDs || props?.setFailedJobs,
    incompleteJobs = props?.incompleteJDs || props?.incompleteJobs || [],
    setIncompleteJobs = props?.setIncompleteJDs || props?.setIncompleteJobs,
    showHeader = true,
    resetJobState,
  } = props || {};

  const [selectedJD, setSelectedJD] = useState(null);
  const results = Array.isArray(jobResults) ? jobResults : [];
  const files = Array.isArray(uploadedFiles) ? uploadedFiles : [];
  const failures = Array.isArray(failedJobs) ? failedJobs : [];
  const incompletes = Array.isArray(incompleteJobs) ? incompleteJobs : [];

  const resultByFile = useMemo(() => {
    const grouped = {};
    results.forEach((result) => {
      const filename = getFileName(result);
      if (!grouped[filename]) grouped[filename] = [];
      const resultId = result.id || result.object_key || `${filename}-${grouped[filename].length}`;
      if (!grouped[filename].some((item) => (item.id || item.object_key) === resultId)) {
        grouped[filename].push(result);
      }
    });
    return grouped;
  }, [results]);

  const fileRows = useMemo(() => {
    const allFiles = [...files];
    [...failures, ...incompletes].forEach((item) => {
      const name = getFileName(item);
      if (!allFiles.some((file) => getFileName(file) === name)) allFiles.push(item);
    });

    return allFiles.map((file, index) => {
      const filename = getFileName(file);
      const records = resultByFile[filename] || [];
      const latest = records[records.length - 1] || failures.find((item) => getFileName(item) === filename) || incompletes.find((item) => getFileName(item) === filename) || {};
      let status = getJobStatus(latest);
      if (incompletes.some((item) => getFileName(item) === filename)) status = "incomplete";
      if (failures.some((item) => getFileName(item) === filename)) status = "failed";

      const preview = parseJobPreview(latest.job_preview);
      return {
        id: latest.id || latest.object_key || `${filename}-${index}`,
        filename,
        record: latest,
        preview,
        job_title: latest.job_title || preview.title || preview.job_title || preview.position_title || "N/A",
        client_name: preview.client_name || latest.client_name || "N/A",
        location: preview.location || latest.location || "N/A",
        status,
        error: latest.metadata?.error || latest.error || file?.error || "",
        metadata: latest.metadata || {},
      };
    });
  }, [files, failures, incompletes, resultByFile]);

  const counts = useMemo(() => {
    const summary = { created: 0, updated: 0, incomplete: 0, failed: 0, processing: 0 };
    fileRows.forEach((row) => { summary[row.status] = (summary[row.status] || 0) + 1; });
    return summary;
  }, [fileRows]);

  const summaryCards = [
    { label: "Total JDs", value: fileRows.length, color: "#6860EE", note: "Files uploaded" },
    { label: "Created", value: counts.created, color: "#4CAF50", note: "Jobs created" },
    { label: "Updated", value: counts.updated, color: "#FF9800", note: "Existing jobs updated" },
    { label: "Incomplete", value: counts.incomplete, color: "#EF6C00", note: "Partial processing" },
    { label: "Failed", value: counts.failed, color: "#F44336", note: "Could not be processed" },
  ];

  const handleBackToUpload = () => {
    if (window.socketRef?.current) {
      window.socketRef.current.close(1000, "User returned to upload page");
      window.socketRef.current = null;
    }
    if (window.countdownIntervalRef?.current) {
      clearInterval(window.countdownIntervalRef.current);
      window.countdownIntervalRef.current = null;
    }
    setJobResults?.([]);
    setUploadedFiles?.([]);
    setFailedJobs?.([]);
    setIncompleteJobs?.([]);
    resetJobState?.();
  };

  const columns = [
    { name: "#", selector: (row, index) => <span>{index + 1}</span>, width: "70px" },
    {
      name: "Job Title",
      selector: (row) => <CustomTooltip title={row.job_title} arrow><span>{row.job_title}</span></CustomTooltip>,
      width: "280px",
    },
    {
      name: "Location",
      selector: (row) => <CustomTooltip title={row.location} arrow><span>{row.location}</span></CustomTooltip>,
      width: "180px",
    },
    {
      name: "JD File",
      selector: (row) => <CustomTooltip title={row.filename} arrow><span className="parsing-file-name-container"><FileIcon fileName={row.filename} />{row.filename}</span></CustomTooltip>,
      width: "220px",
    },
    {
      name: "Status",
      selector: (row) => {
        const color = row.status === "created" ? "#2E7D32" : row.status === "updated" ? "#EF6C00" : row.status === "incomplete" ? "#EF6C00" : row.status === "failed" ? "#D32F2F" : "#777";
        return <CustomTooltip title={row.error || row.status} arrow><span style={{ color, fontSize: "14px", fontWeight: 600, textTransform: "capitalize" }}>{row.status}</span></CustomTooltip>;
      },
      width: "130px",
    },
  ];

  return (
    <>
      <div className="spreadsheet-summary-container" style={{
        background: isDarkMode ? "rgba(30, 30, 40, 0.7)" : "white",
        borderRadius: "12px", padding: "20px", margin: "20px",
        border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", flexWrap: "wrap", marginBottom: "20px" }}>
          <div>
            <h3 style={{ color: isDarkMode ? "#fff" : "#6860EE", margin: 0, fontSize: "18px", fontWeight: 600 }}>Import Summary</h3>
            <p style={{ color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)", fontSize: "14px", margin: "4px 0 0" }}>{fileRows.length} job description file{fileRows.length === 1 ? "" : "s"} uploaded</p>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {summaryCards.map((card) => (
              <div key={card.label} style={{ padding: "12px 16px", background: isDarkMode ? `${card.color}26` : `${card.color}14`, borderRadius: "10px", border: `1px solid ${card.color}40`, minWidth: "112px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", fontWeight: 500, color: isDarkMode ? "rgba(255,255,255,0.7)" : "#666", marginBottom: "4px" }}>{card.label}</div>
                <div style={{ fontSize: "24px", fontWeight: 700, color: card.color, lineHeight: 1 }}>{card.value}</div>
                <div style={{ fontSize: "11px", color: isDarkMode ? "rgba(255,255,255,0.65)" : "#666", marginTop: "4px" }}>{card.note}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ maxHeight: "180px", overflowY: "auto" }}>
          {fileRows.map((row) => (
            <div key={row.id} style={{ padding: "12px", marginBottom: "8px", background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)", borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                <FileIcon fileName={row.filename} />
                <strong style={{ color: isDarkMode ? "#fff" : "#333", fontSize: "14px" }}>{row.filename}</strong>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                {row.metadata.total_jobs != null && <span style={{ color: isDarkMode ? "#bbb" : "#666", fontSize: "12px" }}>{row.metadata.completed_jobs || 0} / {row.metadata.total_jobs} completed</span>}
                {row.error && <CustomTooltip title={row.error} arrow><span style={{ color: row.status === "incomplete" ? "#EF6C00" : "#D32F2F", fontSize: "12px", maxWidth: "360px" }}>{row.error}</span></CustomTooltip>}
                {row.status === "processing" ? <div className="parsed-status-container"><DotLoader size={1} borderWidth={5} duration={1.5} /><span>Processing</span></div> : <span style={{ color: row.status === "created" ? "#2E7D32" : row.status === "updated" || row.status === "incomplete" ? "#EF6C00" : "#D32F2F", fontSize: "12px", fontWeight: 600, textTransform: "capitalize" }}>{row.status}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="main-resume-container">
        <div className="table-container">
          <Table columns={columns} data={fileRows} pagination highlightOnHover fixedHeader fixedHeaderScrollHeight="500px" dataNotFoundMessage="No job description results yet..!" clearSelectedRows={false} backgroundColor="var(--body__background)" color="var(--header__color)" />
        </div>
      </div>

      {selectedJD && (
        <div role="presentation" onClick={() => setSelectedJD(null)} style={{ position: "fixed", inset: 0, zIndex: 1500, background: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "flex-end" }}>
          <div role="dialog" aria-modal="true" aria-label="Job description details" onClick={(event) => event.stopPropagation()} style={{ width: "min(600px, 92vw)", height: "100%", overflowY: "auto", padding: "24px", boxSizing: "border-box", background: isDarkMode ? "#242430" : "#fff", color: isDarkMode ? "#fff" : "#333", boxShadow: "-8px 0 24px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
              <h3 style={{ margin: 0 }}>Job Description Details</h3>
              <button type="button" onClick={() => setSelectedJD(null)} aria-label="Close job description details">✕</button>
            </div>
            <h4 style={{ marginBottom: "16px" }}>{selectedJD.job_title}</h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
              {[["Client", selectedJD.client_name], ["Location", selectedJD.location], ["Job ID", selectedJD.record.job_id || "Not created"], ["Status", selectedJD.status], ["Source File", selectedJD.filename], ["File ID", selectedJD.record.id || "N/A"]].map(([label, value]) => (
                <div key={label}><div style={{ fontSize: "12px", opacity: 0.65, marginBottom: "4px" }}>{label}</div><div style={{ fontSize: "14px", overflowWrap: "anywhere" }}>{value}</div></div>
              ))}
            </div>
            {selectedJD.error && <div style={{ padding: "12px", borderRadius: "8px", background: "rgba(244,67,54,0.1)", color: "#D32F2F", marginBottom: "16px" }}><strong>Processing error:</strong> {selectedJD.error}</div>}
            <h4>Extracted Job Data</h4>
            <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", padding: "14px", borderRadius: "8px", background: isDarkMode ? "rgba(0,0,0,0.25)" : "#f5f5f8", fontSize: "12px" }}>{JSON.stringify(selectedJD.preview, null, 2)}</pre>
            <h4>Raw WebSocket Record</h4>
            <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", padding: "14px", borderRadius: "8px", background: isDarkMode ? "rgba(0,0,0,0.25)" : "#f5f5f8", fontSize: "12px" }}>{JSON.stringify(selectedJD.record, null, 2)}</pre>
          </div>
        </div>
      )}
    </>
  );
}

export default JobJDResults;